import { gql, useQuery } from "@apollo/client";
import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { IMetaobjectByHandleQuery } from "@repo/shopify/queries/metaobject-gql";
import { metaobjectByHandleQuery } from "@repo/shopify/queries/metaobject-gql";
import type { MediaImage, MetaobjectField } from "@shopify/hydrogen-react/storefront-api-types";
import classNames from "classnames";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";

import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import { DEFAULT_TITLE } from "@const/meta";
import { NOTIFICATIONS } from "@const/notifications";

import { useLayoutStore } from "@store/layout";

function StoresDetail() {
  const { handle } = useParams();

  const { openToast, setMeta } = useLayoutStore();

  const { isPC } = useCheckDevice();

  const { data, loading } = useQuery<IMetaobjectByHandleQuery>(gql(metaobjectByHandleQuery.query), {
    variables: { handle: { handle, type: "store" } },
    fetchPolicy: "no-cache",
    onCompleted: (_data) => {
      const title = _data?.metaobject.fields.find((item) => item.key === "title");
      setMeta({ title: `${title?.value?.replace("\\n", " ")} - ${DEFAULT_TITLE}` });
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [handle]);

  const getValue = (node: MetaobjectField[], key: string) => {
    return node?.find((item) => item.key === key);
  };

  return (
    <>
      {loading || !data ? (
        <Loading type="page" />
      ) : (
        <>
          <section className="pc:col-span-12 col-span-8 flex justify-between">
            <div className="text-title font-features-normal flex flex-col whitespace-pre-line">
              {getValue(data?.metaobject?.fields, "title")?.value?.replace(/\\n/g, "\n")}
            </div>
            <Link
              as="text"
              href={(JSON.parse(getValue(data?.metaobject?.fields, "location")?.value || "[]") as [string, string])[1]}
            >
              View Map
            </Link>
          </section>
          <section className="pc:col-span-12 col-span-8 row-start-3 flex flex-col gap-10">
            <section className="pc:col-span-12 col-span-8 row-start-3 flex flex-col gap-1">
              <div className="tnt-gap-x pc:grid-cols-12 grid grid-cols-8">
                <span className="pc:col-span-1 col-span-2">Address</span>
                <Link
                  as="text"
                  className="pc:col-span-12 col-span-4 w-fit"
                  href={
                    (JSON.parse(getValue(data?.metaobject?.fields, "location")?.value || "[]") as [string, string])[1]
                  }
                >
                  {(JSON.parse(getValue(data?.metaobject?.fields, "location")?.value || "[]") as [string, string])[0]}
                </Link>
              </div>
              <div className="tnt-gap-x pc:grid-cols-12 grid grid-cols-6">
                <span className="pc:col-span-1 col-span-2">Phone</span>
                <Link
                  as="text"
                  className="pc:col-span-12 col-span-4 w-fit"
                  href={`tel:${getValue(data?.metaobject?.fields, "tel")?.value}`}
                >
                  {getValue(data?.metaobject?.fields, "tel")?.value}
                </Link>
              </div>
              <div className="tnt-gap-x pc:grid-cols-12 grid grid-cols-6">
                <span className="pc:col-span-1 col-span-2">Opening Hours</span>
                <span className="pc:col-span-12 col-span-4 whitespace-pre-line">
                  {getValue(data?.metaobject?.fields, "opening_hour")?.value?.replace(/\\n/g, "\n")}
                </span>
              </div>
            </section>
            <section className="pc:col-span-12 pc:-translate-x-5 pc:grid-cols-12 col-span-8 grid w-screen -translate-x-4 grid-cols-6 gap-0.5">
              {getValue(data.metaobject.fields, "images")?.references?.edges.map(({ node }, index) => (
                <Image
                  className={classNames(
                    index === 0 && isPC ? "col-span-10" : "pc:col-span-5 col-span-8",
                    "aspect-5/4!",
                  )}
                  height={(node as MediaImage).image?.height || 2000}
                  key={(node as MediaImage).image?.id}
                  src={(node as MediaImage).image?.url || ""}
                  width={(node as MediaImage).image?.width || 2500}
                />
              ))}
            </section>
          </section>
        </>
      )}
    </>
  );
}

export default StoresDetail;
